import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Send } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { fmtDate } from '../lib/dates';
import { InstructorInvoice } from '../types';
import { Badge, Button, Callout, Card, TextField, useToast } from '../components/charte';

// Mes factures (18/10/2026) et notes de frais (20/10/2026) : un moniteur indépendant envoie la facture de
// ses prestations (fichier facultatif, montant au moins) ; un salarié envoie une note de frais pour des frais
// avancés (justificatif, montant et motif obligatoires). PDF, JPEG ou PNG de 10 Mo au plus. Le secrétariat
// les retrouve dans « Moniteurs » et les marque traitées.

const MAX_BYTES = 10 * 1024 * 1024;
const euros = (cents: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);
type Kind = 'invoice' | 'expense';

function SendForm({ kind }: { kind: Kind }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const expense = kind === 'expense';
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState('');
  const [amount, setAmount] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => instructorApi.sendInvoice(file, { reference: expense ? '' : reference, amount, issuedOn, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-invoices'] });
      setFile(null);
      setReference('');
      setAmount('');
      setIssuedOn('');
      setNote('');
      if (input.current) input.current.value = '';
      toast(expense ? 'Note de frais envoyée au secrétariat.' : 'Facture envoyée au secrétariat.');
    },
    onError: (e: Error) => setError(e.message || 'Envoi impossible.'),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (file && file.size > MAX_BYTES) return setError('Fichier trop lourd : 10 Mo au plus.');
    if (expense) {
      if (!file) return setError('Joignez le justificatif de la dépense (ticket ou facture du fournisseur : PDF, JPEG ou PNG).');
      if (!amount.trim()) return setError('Indiquez le montant de la dépense.');
      if (!note.trim()) return setError('Indiquez le motif de la dépense (carburant, péage…).');
    } else if (!file && !amount.trim()) return setError('Sans fichier, indiquez au moins le montant de la facture.');
    setError(null);
    send.mutate();
  };
  return (
    <Card className="p-5 sm:p-6">
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <label className="grid gap-1.5">
          <span className="text-[12.5px] font-medium text-secondary">
            {expense ? 'Justificatif : ticket ou facture du fournisseur (PDF, JPEG ou PNG) *' : 'Facture (PDF, JPEG ou PNG), facultative'}
          </span>
          <input
            ref={input}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full rounded-xl border border-line bg-surface-light px-3 py-2.5 text-sm text-heading file:mr-3 file:rounded-lg file:border-0 file:bg-surface-indigo file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary"
          />
        </label>
        {expense ? (
          <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
            <TextField label="Motif de la dépense *" maxLength={500} placeholder="Carburant, péage…" value={note} onChange={(e) => setNote(e.target.value)} />
            <TextField label="Montant (€) *" inputMode="decimal" placeholder="62,40" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <TextField label="Date de la dépense" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-1">
            <TextField label="N° de facture" maxLength={64} value={reference} onChange={(e) => setReference(e.target.value)} />
            <TextField label={file ? 'Montant (€)' : 'Montant (€) *'} inputMode="decimal" placeholder="450,00" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <TextField label="Date de la facture" type="date" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} />
          </div>
        )}
        {error && (
          <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" icon={Send} loading={send.isPending} className="h-11 sm:justify-self-start">
          Envoyer au secrétariat
        </Button>
      </form>
    </Card>
  );
}

function InvoiceRow({ invoice }: { invoice: InstructorInvoice }) {
  const toast = useToast();
  const download = async () => {
    try {
      const blob = await instructorApi.downloadInvoice(invoice.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = invoice.filename ?? 'facture';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast('Téléchargement impossible.');
    }
  };
  return (
    <li>
      <Card className="flex flex-wrap items-center gap-3 p-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
          <FileText size={18} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-heading">
            {invoice.kind === 'expense'
              ? `Note de frais${invoice.note ? ` · ${invoice.note}` : ''}`
              : invoice.reference
                ? `Facture ${invoice.reference}`
                : (invoice.filename ?? 'Facture')}
            {invoice.amountCents !== null && ` · ${euros(invoice.amountCents)}`}
          </p>
          <p className="text-[13px] text-secondary">
            Envoyée le {fmtDate(invoice.submittedAt)}
            {invoice.issuedOn && ` · datée du ${fmtDate(invoice.issuedOn)}`}
            {!invoice.filename && ' · sans fichier'}
          </p>
        </div>
        <Badge tone={invoice.status === 'processed' ? 'ok' : 'todo'}>{invoice.status === 'processed' ? 'Traitée' : 'Reçue'}</Badge>
        {invoice.filename && (
          <Button size="sm" variant="ghost" icon={Download} onClick={() => void download()}>
            Télécharger
          </Button>
        )}
      </Card>
    </li>
  );
}

export default function InvoicesView() {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['my-invoices'], queryFn: instructorApi.getInvoices });
  const expense = data?.kind === 'expense';
  return (
    <section className="mx-auto grid max-w-3xl gap-5">
      <header>
        <h1 className="text-title-md text-heading sm:text-title-lg">{expense ? 'Mes notes de frais' : 'Mes factures'}</h1>
        <p className="mt-1 text-sm text-secondary">
          {expense
            ? 'Frais avancés pour l’auto-école : envoyez la note de frais au secrétariat, justificatif joint.'
            : 'Envoyez la facture de vos prestations au secrétariat de l’auto-école.'}
        </p>
      </header>
      {isLoading && <p className="text-secondary">Chargement…</p>}
      {isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Impossible de charger vos envois : {(error as Error).message}
        </p>
      )}
      {data && !data.enabled && <Callout tone="warn">{data.reason ?? 'Envoi indisponible.'}</Callout>}
      {data?.enabled && <SendForm kind={data.kind ?? 'invoice'} />}
      {data && (data.items?.length ?? 0) > 0 && (
        <div className="grid gap-3">
          <h2 className="text-title-sm text-heading">{expense ? 'Envois au secrétariat' : 'Factures envoyées'}</h2>
          <ul className="grid gap-2">
            {data.items.map((invoice) => (
              <InvoiceRow key={invoice.id} invoice={invoice} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
